import axios, { type AxiosInstance, isAxiosError } from 'axios';
import { baseUrl } from '../../base';
import type {
  ApiErrorBody,
  ChangeStatusRequest,
  ChangeStatusResponse,
  MessageOption,
  MessageOptionInput,
  StatusChangeContext,
  StatusNotificationConfig,
  StatusNotificationSummary,
  TestSendRequest,
  TestSendResponse,
  UpdateStatusNotificationRequest,
} from './roverNotifications.types';

/**
 * Optional dev mock target. The live client below uses the app axios instance so a mock
 * adapter on this instance cannot intercept status changes or notification saves.
 */
export const http: AxiosInstance = axios.create({
  withCredentials: true,
});

http.interceptors.request.use((config) => {
  if (!config.baseURL) {
    config.baseURL = axios.defaults.baseURL;
  }
  const subdomain = axios.defaults.headers.common['x-subdomain'];
  if (subdomain && !config.headers?.['x-subdomain']) {
    config.headers.set('x-subdomain', String(subdomain));
  }
  return config;
});

// Dev mock: set VITE_ROVER_NOTIFICATIONS_MOCK=true and see main.tsx bootstrap.

type Envelope<T> = { data: T };

const base = (formId: string) => `${baseUrl.ROVER}/forms/${encodeURIComponent(formId)}/notifications`;
const statusPath = (formId: string, status: string) => `${base(formId)}/${encodeURIComponent(status)}`;

export const roverNotificationsApi = {
  listStatuses: async (formId: string) => (await axios.get<Envelope<StatusNotificationSummary[]>>(base(formId))).data.data,

  getStatus: async (formId: string, status: string) =>
    (await axios.get<Envelope<StatusNotificationConfig>>(statusPath(formId, status))).data.data,

  updateStatus: async (formId: string, status: string, body: UpdateStatusNotificationRequest) =>
    (await axios.patch<Envelope<StatusNotificationConfig>>(statusPath(formId, status), body)).data.data,

  resetStatus: async (formId: string, status: string) =>
    (await axios.post<Envelope<StatusNotificationConfig>>(`${statusPath(formId, status)}/reset`)).data.data,

  listOptions: async (formId: string, status: string) =>
    (await axios.get<Envelope<MessageOption[]>>(`${statusPath(formId, status)}/options`)).data.data,

  createOption: async (formId: string, status: string, body: MessageOptionInput) =>
    (await axios.post<Envelope<MessageOption>>(`${statusPath(formId, status)}/options`, body)).data.data,

  updateOption: async (formId: string, status: string, optionId: string, body: Partial<MessageOptionInput>) =>
    (await axios.patch<Envelope<MessageOption>>(`${statusPath(formId, status)}/options/${encodeURIComponent(optionId)}`, body)).data.data,

  deleteOption: async (formId: string, status: string, optionId: string) => {
    await axios.delete(`${statusPath(formId, status)}/options/${encodeURIComponent(optionId)}`);
  },

  reorderOptions: async (formId: string, status: string, optionIds: string[]) =>
    (await axios.put<Envelope<MessageOption[]>>(`${statusPath(formId, status)}/options/order`, { optionIds })).data.data,

  sendTest: async (formId: string, status: string, body: TestSendRequest) =>
    (await axios.post<Envelope<TestSendResponse>>(`${statusPath(formId, status)}/test`, body)).data.data,

  getStatusChangeContext: async (applicationId: string, status: string) =>
    (
      await axios.get<Envelope<StatusChangeContext>>(
        `${baseUrl.ROVER}/applications/${encodeURIComponent(applicationId)}/status-change-context`,
        { params: { status } }
      )
    ).data.data,

  changeStatus: async (applicationId: string, body: ChangeStatusRequest) =>
    (await axios.post<Envelope<ChangeStatusResponse>>(`${baseUrl.ROVER}/applications/${encodeURIComponent(applicationId)}/status`, body))
      .data.data,
};

/** Pulls the message (and field errors) out of the standard API error shape. */
export function readApiError(err: unknown): { message: string; fields?: Record<string, string> } {
  if (isAxiosError<ApiErrorBody>(err) && err.response?.data?.error) {
    const { message, fields } = err.response.data.error;
    return { message, fields };
  }
  return { message: 'Something went wrong. Please try again.' };
}
